/**
 * Firebase Logout Service
 *
 * Handles user logout from Firebase Authentication and clears local state.
 *
 * @module services/firebase/logout
 */

import { signOut } from "firebase/auth"

import { useFleetHubStore } from "@/stores/useFleetHubStore"

import { auth } from "./auth"

/**
 * Logout current user from Firebase and clear FleetHub state
 *
 * This function:
 * 1. Signs out the user from Firebase Authentication
 * 2. Stops live tracking and cleans up Pusher connections
 * 3. Clears any cached FleetHub order data
 * 4. Resets the FleetHub store to initial state
 *
 * @throws {Error} If logout fails
 */
export async function logoutUser(): Promise<void> {
  try {
    // Sign out from Firebase
    await signOut(auth)

    // CRITICAL: Stop live tracking and cleanup Pusher connections FIRST
    // This ensures all websocket connections are properly closed before state reset
    const stopTracking = useFleetHubStore.getState().stopLiveTracking
    if (stopTracking) {
      stopTracking()
    }

    // THEN clear FleetHub store data
    const resetStore = useFleetHubStore.getState().reset
    if (resetStore) {
      resetStore()
    }

    console.log("User logged out successfully")
  } catch (error: any) {
    console.error("Logout failed:", error)
    throw new Error(`Logout fehlgeschlagen: ${error.message}`)
  }
}
